import { getItemOrDefaultToValue, nestedDisplay } from '../../details';
import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import { intervalMapping } from 'utils/constants/patrolSchedule';
import {
  conversionOfTime,
  formatDuration,
  getTimeInMins,
  systemTimezone,
} from 'utils/time';
import patrolIcon from 'images/icon_service_patrol.svg';
import get from 'lodash/get';
import { endTime, everyDaySchedule, startTime } from 'mocks/utils';
import isEqual from 'lodash/isEqual';
import {
  siteRequiresPassKey,
  siteRequiresPhotoIdKey,
  siteRequiresPhotoKey,
  VISITOR_PASS_TYPE,
  VISITOR_PHOTO_TYPE,
} from '../../constants/visitor';
import { DEFAULT_EMPTY_VALUE } from 'utils/constants';
import { setSiteName } from 'utils/helper/site';

const transformToMapMarkers = (firstLocation, locationArray) => {
  let fullLocations = [];

  if (locationArray.length > 0) {
    fullLocations = [firstLocation, ...locationArray];
  } else {
    fullLocations = [firstLocation];
  }

  return fullLocations.reduce((current, location, index) => {
    if (!location.point.isWaypoint) {
      return current.concat({
        seqNumber: index + 1,
        qrCode: location.qrCode,
        lat: location.point.latitude,
        lng: location.point.longitude,
        type: 'CHECKPOINT',
        isWaypoint: location.point.isWaypoint,
        text: location.checkPointSequence,
        disabled: index === 0,
        evidence: location.evidence,
        taskId: location.taskId,
        patrolled: false,
        isPatrolDisabled: index !== 0,
        outsideTaskId: location?.outsideTaskId,
      });
    } else {
      const newArray = current.concat({
        seqNumber: index + 1,
        lat: location.point.latitude,
        lng: location.point.longitude,
        isWaypoint: location.point.isWaypoint,
        type: 'WAYPOINT',
        text: null,
        disabled: false,
        evidence: location.evidence,
        taskId: location.taskId,
      });
      return newArray;
    }
  }, []);
};

const getAllDayData = data => {
  return data.schedule &&
    data.schedule.startTime != null &&
    data.schedule &&
    data.schedule.endTime != null
    ? conversionOfTime(data.schedule.startTime, 5) === startTime &&
        conversionOfTime(data.schedule.endTime, 5) === endTime
    : false;
};

const transformCheckpointsToPixels = (mapToMarkers, imageDimensions) => {
  return mapToMarkers.map(m => {
    const latitude = parseFloat(
      (imageDimensions.height - m.lat * imageDimensions.height).toFixed(2),
    );
    const longitide = parseFloat((m.lng * imageDimensions.width).toFixed(2));
    return {
      ...m,
      lng: latitude,
      lat: longitide,
    };
  });
};

const checkPoints = (data, tf) => {
  const mapToMarkers = transformToMapMarkers(
    getItemOrDefaultToValue(data, 'firstLocation'),
    getItemOrDefaultToValue(data, 'locations', []),
    tf,
  );

  if (data.surfaceId && data.surfaceId !== undefined) {
    return transformCheckpointsToPixels(mapToMarkers, data.imageDimensions);
  } else {
    return mapToMarkers;
  }
};
const setImageOrMap = data => {
  if (data.surfaceId !== null) {
    return data.surfaces.filter(f => f.id === data.surfaceId)[0]['imageUrl'];
  } else if (data.surfaceId === null && data.imageUrl) {
    return getItemOrDefaultToValue(data, 'imageUrl');
  } else {
    return patrolIcon;
  }
};
export const transformPatrolDefDetails = ({
  data,
  siteList,
  globalDepartmentList = [],
}) => {
  const defDetail = find(siteList, {
    id: get(data, 'site.id'),
  });

  const selectedDepartments = globalDepartmentList.filter(f =>
    getItemOrDefaultToValue(data, 'departmentIds', []).includes(f.id),
  );

  const checkReturnToStart = data => {
    const cp = checkPoints(data, false);
    if (cp.length > 1) {
      // compare the first and the last value in the array
      if (
        cp[0].lat === cp[cp.length - 1].lat &&
        cp[0].lng === cp[cp.length - 1].lng
      ) {
        // add returnToStart flag to true

        return true;
      } else {
        // add returnToStart flag to true

        return false;
      }
    } else {
      // add returnToStart flag to true

      return false;
    }
  };

  return {
    generalInfo: {
      patrolName: {
        type: 'text',
        value: getItemOrDefaultToValue(data, 'definitionName', '-'),
      },
      mobilePatrol: {
        type: 'boolean',
        value: getItemOrDefaultToValue(data, 'isMultiSite', false),
      },
      businessClient: {
        type: 'text',
        value: getItemOrDefaultToValue(
          data?.businessClient,
          'businessClientName',
          '-',
        ),
      },
      site: {
        type: 'text',
        value:
          data.site !== null
            ? getItemOrDefaultToValue(data.site, 'siteName', '-')
            : '-',
      },
      virtualPatrol: {
        type: 'image-boolean',
        value: get(data, 'proximityAlertDisabled'),
      },
      departments: {
        type: 'array',
        label: 'Departments',
        value: nestedDisplay(selectedDepartments),
      },
    },
    detailedInfo: {
      timezone: {
        type: 'text',
        value: getItemOrDefaultToValue(data, 'timezone', systemTimezone),
      },
      schedule: {
        type: 'text',
        value:
          data.schedule &&
          data.schedule.days !== null &&
          data.schedule.days.length > 0
            ? getItemOrDefaultToValue(data.schedule, 'days', [])
                .map(day => capitalize(day))
                .join(', ')
            : '-',
      },
      operationalHours: {
        type: 'text',
        value:
          data.schedule && (data.schedule.startTime || data.schedule.endTime)
            ? `${conversionOfTime(
                getItemOrDefaultToValue(data.schedule, 'startTime', '00:00'),
                5,
              )} - ${conversionOfTime(
                getItemOrDefaultToValue(data.schedule, 'endTime', '-'),
                5,
              )}`
            : '-',
      },
      expectedDuration: {
        type: 'text',
        value: data.expectedDuration
          ? formatDuration(getItemOrDefaultToValue(data, 'expectedDuration'))
          : DEFAULT_EMPTY_VALUE,
      },
      interval: {
        type: 'text',
        value:
          data.schedule &&
          data.schedule.interval !== null &&
          data.schedule.interval !== ''
            ? intervalMapping[
                getItemOrDefaultToValue(data.schedule, 'interval', '-')
              ]
            : '-',
      },
      patrolledLocations: {
        type: 'text',
        label: 'Patrolled Locations',
        value: getItemOrDefaultToValue(data, 'patrolledLocations', []),
      },

      checkPointDetail: {
        type: data.surfaceId ? 'image-marker' : 'map-marker',
        label: 'Check Point Detail',
        additionalPolygon: defDetail ? defDetail.locationDetail : [],
        value: checkPoints(data, false),
        center: defDetail
          ? defDetail.centreOfGravity
            ? defDetail.centreOfGravity
            : data.firstLocation.point
          : data.firstLocation.point,
        boundryList: checkPoints(data),
        ...(data.surfaceId !== null &&
          data.surfaces && {
            // add imageUrl as a field incase the surfaceId and surfaces are set
            imageUrl: data.surfaces.filter(f => f.id === data.surfaceId)[0][
              'imageUrl'
            ],
          }),
      },
      returnToStart: {
        type: 'image-boolean',
        value: checkReturnToStart(data),
      },
    },
    others: {
      // Others pre fill values when the forms are open in edit mode
      returnToStart: {
        type: 'image-boolean',
        value: checkReturnToStart(data),
      },
      image: {
        value: setImageOrMap(data),
      },
      ...(data.imageDimensions && {
        imageDimensions: {
          value: data.imageDimensions,
        },
      }),
      id: {
        value: getItemOrDefaultToValue(data, 'id'),
      },
      patrolDefinitionName: {
        value: getItemOrDefaultToValue(data, 'definitionName'),
      },
      archived: {
        value: getItemOrDefaultToValue(data, 'archived', false),
      },
      checkpoints: {
        value: checkPoints(
          data,
          getItemOrDefaultToValue(data, 'locations', []).length > 0,
        ),
      },
      siteSurfaceImages: {
        value: data.siteSurfaceImages,
      },
      mobilePatrol: {
        type: 'image-boolean',
        value: get(data, 'isMultiSite'),
      },
      schedule: {
        value:
          data.schedule &&
          data.schedule.days != null &&
          data.schedule.days.length > 0
            ? get(data, 'schedule.days', [])
            : undefined,
      },
      startTime: {
        value:
          data.schedule && data.schedule.startTime != null
            ? conversionOfTime(
                getItemOrDefaultToValue(data.schedule, 'startTime', '-'),
                5,
              )
            : undefined,
      },
      endTime: {
        value:
          data.schedule && data.schedule.endTime != null
            ? conversionOfTime(
                getItemOrDefaultToValue(data.schedule, 'endTime', '-'),
                5,
              )
            : undefined,
      },
      interval: {
        value:
          data.schedule &&
          data.schedule.interval != null &&
          data.schedule.interval.length > 0
            ? get(data, 'schedule.interval')
            : undefined,
      },
      expectedDuration: {
        value: getTimeInMins(getItemOrDefaultToValue(data, 'expectedDuration')),
      },
      site: {
        value: {
          ...defDetail,
          name: setSiteName(defDetail),
        },
      },
      allDay: {
        value: getAllDayData(data),
      },
      everyDay: {
        value:
          data.schedule &&
          data.schedule.days != null &&
          data.schedule.days.length > 0
            ? isEqual(everyDaySchedule, data.schedule.days)
            : false,
      },
      [siteRequiresPassKey]: {
        type: 'text',
        value: VISITOR_PASS_TYPE.NO.id,
      },
      [siteRequiresPhotoIdKey]: {
        type: 'text',
        value: VISITOR_PHOTO_TYPE.OPTIONAL.id,
      },
      [siteRequiresPhotoKey]: {
        type: 'text',
        value: VISITOR_PHOTO_TYPE.OPTIONAL.id,
      },
      siteInfo: {
        value:
          data.site !== null
            ? getItemOrDefaultToValue(data, 'site', null)
            : '-',
      },
      businessClientInfo: {
        value:
          data.businessClient !== null
            ? getItemOrDefaultToValue(data, 'businessClient', null)
            : '-',
      },
      firstLocation: {
        value:
          data.firstLocation !== null
            ? getItemOrDefaultToValue(data, 'firstLocation', null)
            : '-',
      },
    },
  };
};
